import { useState } from "react";
import type { Editor } from "./canvas";
import type { Sheet } from "../shared/schema";
import {
  isSheetPreset,
  sheetDimensionsMm,
  sheetSizeChoices,
  type SheetSizeChoice,
} from "../shared/sheets";

const millimetres = (units: number) =>
  String(Number(((units * 3) / 8).toFixed(1)));

export function SheetSizeControls({
  editor,
  sheet,
  disabled: disabledByParent = false,
}: {
  editor: Editor;
  sheet: Sheet;
  disabled?: boolean;
}) {
  const [paper, setPaper] = useState<SheetSizeChoice>(
    isSheetPreset(sheet.props.paper) ? sheet.props.paper : "Custom",
  );
  const [width, setWidth] = useState(millimetres(sheet.props.w));
  const [height, setHeight] = useState(millimetres(sheet.props.h));
  const [landscape, setLandscape] = useState(sheet.props.w >= sheet.props.h);
  const disabled =
    disabledByParent ||
    editor.getIsReadonly() ||
    sheet.isLocked ||
    !!sheet.props.source;
  const widthMm = Number(width);
  const heightMm = Number(height);
  const valid =
    Number.isFinite(widthMm) &&
    Number.isFinite(heightMm) &&
    widthMm >= 50 &&
    widthMm <= 2000 &&
    heightMm >= 50 &&
    heightMm <= 2000;

  return (
    <form
      className="sheet-size-controls"
      onSubmit={(event) => {
        event.preventDefault();
        if (disabled || !valid) return;
        editor.markHistoryStoppingPoint("Resize paper");
        editor.updateShape({
          id: sheet.id,
          type: "sheet",
          props: { w: (widthMm * 8) / 3, h: (heightMm * 8) / 3, paper },
          meta: {
            ...sheet.meta,
            paperWidthMm: widthMm,
            paperHeightMm: heightMm,
          },
        });
      }}
    >
      <div className="sheet-size-row">
        <label>
          Format
          <select
            aria-label="Sheet size"
            value={paper}
            disabled={disabled}
            onChange={(event) => {
              const choice = event.target.value as SheetSizeChoice;
              setPaper(choice);
              if (choice === "Custom") return;
              const nextLandscape = choice !== "A4" && choice !== "Letter";
              const next = sheetDimensionsMm(choice, nextLandscape);
              setLandscape(nextLandscape);
              setWidth(String(next.widthMm));
              setHeight(String(next.heightMm));
            }}
          >
            {sheetSizeChoices.map((choice) => (
              <option key={choice} value={choice}>
                {choice}
              </option>
            ))}
          </select>
        </label>
        {paper !== "Custom" && paper !== "1:1" && (
          <label>
            Orientation
            <select
              disabled={disabled}
              value={landscape ? "landscape" : "portrait"}
              onChange={(event) => {
                const nextLandscape = event.target.value === "landscape";
                const next = sheetDimensionsMm(paper, nextLandscape);
                setLandscape(nextLandscape);
                setWidth(String(next.widthMm));
                setHeight(String(next.heightMm));
              }}
            >
              <option value="landscape">Landscape</option>
              <option value="portrait">Portrait</option>
            </select>
          </label>
        )}
      </div>
      {paper === "Custom" ? (
        <div className="form-grid">
          <label>
            Width (mm)
            <input
              aria-label="Sheet width in millimetres"
              type="number"
              disabled={disabled}
              min="50"
              max="2000"
              step="any"
              required
              value={width}
              onChange={(event) => {
                setWidth(event.target.value);
                setPaper("Custom");
              }}
            />
          </label>
          <label>
            Height (mm)
            <input
              aria-label="Sheet height in millimetres"
              type="number"
              disabled={disabled}
              min="50"
              max="2000"
              step="any"
              required
              value={height}
              onChange={(event) => {
                setHeight(event.target.value);
                setPaper("Custom");
              }}
            />
          </label>
        </div>
      ) : (
        <small className="sheet-size-summary">
          {width} × {height} mm
        </small>
      )}
      <button type="submit" disabled={disabled || !valid}>
        Resize paper
      </button>
      {sheet.props.source && (
        <small>Resize imported plans with a corner handle.</small>
      )}
    </form>
  );
}
